import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";
import { FaPercentage, FaChartLine, FaClipboardCheck, FaHandHoldingUsd, FaRegCalendarAlt } from "react-icons/fa";

const InterestRatePolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Fair Pricing Model",
      icon: <FaChartLine />,
      content: `${import.meta.env.VITE_COMPANY_NBFC} (Operating as ${import.meta.env.VITE_COMPANY_NAME}) follows a board-approved interest rate model. We factor in the cost of funds, operational margins, and individual risk premiums to ensure fair and competitive pricing for all our borrowers.`,
    },
    {
      title: "2. Absolute Disclosure",
      icon: <FaClipboardCheck />,
      content: "Transparency is our promise. All interest rates, processing fees, and applicable charges are disclosed upfront on our website and in your personalized sanction letter. We ensure you have all the facts before you commit.",
    },
    {
      title: "3. Responsible Lending",
      icon: <FaPercentage />,
      list: [
        "Risk-Based Pricing: Rates are tailored to your credit score and financial profile.",
        "Fixed Rates: All our loans feature fixed interest rates for total predictability.",
        "Regulatory Caps: We strictly adhere to RBI guidelines on interest rate ceilings and fair practices.",
      ],
    },
    {
      title: "4. Fees & Transparency",
      icon: <FaHandHoldingUsd />,
      list: [
        "Processing Fees: Typically 10–15%, clearly communicated during the application.",
        "No Hidden Costs: All EMI returns or rescheduling fees are disclosed in your loan agreement.",
        "Policy Updates: Any changes to our fee structure are purely prospective and clearly published.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="Interest Rate Policy"
        description={`Understand our fair and transparent interest rate model. At ${import.meta.env.VITE_COMPANY_NAME}, we prioritize responsible lending and clear disclosure.`}
        keywords={`interest rate policy, fair lending, loan charges, fintech transparency`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/30 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          <header className="mb-20">
             <div className="flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl w-fit rounded-full mb-10 transition-transform hover:scale-105">
                <FaRegCalendarAlt className="text-secondary text-[10px]" />
                <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">Rate Standards 2024-25</span>
              </div>
            <h1 className="text-4xl md:text-7xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Fair <br />
              <span className="text-secondary">Transparency.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-xl font-medium leading-relaxed max-w-2xl">
              We believe in honest interest rates. Our policy is designed to ensure that credit is affordable, accessible, and governed by the highest ethical standards.
            </p>
          </header>

          <div className="space-y-12">
            {policySections.map((section, idx) => (
              <div key={idx} className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0">
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-4xl font-black mb-6 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-text-secondary text-lg md:text-xl leading-relaxed font-medium mb-6">
                        {section.content}
                      </p>
                    )}
                    {section.list && (
                      <ul className="space-y-4 pt-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-lg leading-relaxed">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Regulatory Footer */}
            <div className="bg-primary text-white p-12 md:p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-[80px]" />
                <h3 className="text-2xl font-black mb-6">Governed by RBI</h3>
                <p className="text-white/80 text-lg leading-relaxed font-medium">
                  This policy is formulated in accordance with the RBI Master Directions (DNBR. PD. 007/03.10.119/2016-17). At {import.meta.env.VITE_COMPANY_NAME}, we ensure that our pricing models are audited and compliant with all fair practice codes.
                </p>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InterestRatePolicy;
